// Ported from packages/workflow-engine-upstream/ui/src/Calls.tsx at commit bebc382; only imports changed.
// Every call the engine made for this run, in order: model calls (what was sent and what came
// back) and tool calls (input and result), with the run's own events between them.
import { useState } from 'react';
import type { CallEntry, RunEvent, RunState } from './api';

type Item = { at: string; order: number; call?: CallEntry; event?: RunEvent };
type Filter = 'all' | 'model' | 'tool' | 'event';

const json = (v: unknown) => JSON.stringify(v, null, 2);

function summary(it: Item): string {
  const c = it.call;
  if (c?.kind === 'model') {
    const usage = (c.output as { usage?: { inputTokens: number; outputTokens: number } } | undefined)?.usage;
    return c.ok ? `${usage?.inputTokens ?? '?'} tokens in · ${usage?.outputTokens ?? '?'} out` : `failed: ${c.error}`;
  }
  if (c) {
    const res = c.output as { outcome?: string; reason?: string; rows?: unknown[] } | undefined;
    const input = c.input as { tags?: string[]; query?: string; question?: string };
    const what = input.tags?.join(', ') ?? input.query ?? input.question ?? '';
    return `${what} → ${res?.outcome ?? 'threw'}${res?.reason ? ` (${res.reason})` : ''}${res?.rows ? ` · ${res.rows.length} rows` : ''}${c.error ? ` ${c.error}` : ''}`;
  }
  const e = it.event!;
  const p = e.payload ? Object.entries(e.payload).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(', ') : '';
  return p;
}

function Detail({ it }: { it: Item }) {
  const c = it.call;
  if (!c) return <pre>{json(it.event)}</pre>;
  if (c.kind === 'tool') {
    return (
      <>
        <span className="h">Sent to the {c.name} tool</span>
        <pre>{json(c.input)}</pre>
        <span className="h">Result</span>
        <pre className={c.ok ? '' : 'err'}>{c.error ?? json(c.output)}</pre>
      </>
    );
  }
  const input = c.input as { messages: { role: string; content: string }[]; schema: unknown; maxOutputTokens: number; maxThinkingTokens: number };
  return (
    <>
      {input.messages.map((m, i) => (
        <div key={i} style={{ display: 'grid', gap: 4 }}>
          <span className="h">
            {m.role} message · {m.content.length} characters
          </span>
          <pre>{m.content}</pre>
        </div>
      ))}
      <details>
        <summary className="note">
          Answer format the model must follow (JSON schema) · at most {input.maxOutputTokens} tokens, thinking {input.maxThinkingTokens ? `${input.maxThinkingTokens} tokens` : 'off'}
        </summary>
        <pre>{json(input.schema)}</pre>
      </details>
      <span className="h">What the model returned</span>
      <pre className={c.ok ? '' : 'err'}>{c.error ?? json(c.output)}</pre>
    </>
  );
}

export function Calls({ s }: { s: RunState }) {
  const [filter, setFilter] = useState<Filter>('all');
  const items: Item[] = [
    ...s.calls.map((call) => ({ at: call.at, order: call.order, call })),
    ...s.events.map((event) => ({ at: s.eventAt[event.seq] ?? event.at, order: s.eventOrder[event.seq] ?? Number.MAX_SAFE_INTEGER, event })),
  ].sort((a, b) => a.order - b.order);
  const t0 = items.length ? Math.min(...items.map((it) => Date.parse(it.at))) : 0;
  const shown = items.filter((it) => filter === 'all' || (filter === 'event' ? !!it.event : it.call?.kind === filter));
  const count = (f: Filter) => items.filter((it) => (f === 'event' ? !!it.event : it.call?.kind === f)).length;
  return (
    <section className="calls">
      <div className="bar" style={{ border: 0, padding: 0 }}>
        <span className="h grow">Every call the engine made · {s.calls.length} calls, {s.events.length} run events</span>
        {(['all', 'model', 'tool', 'event'] as Filter[]).map((f) => (
          <button key={f} type="button" className={filter === f ? 'chip on' : 'chip'} onClick={() => setFilter(f)}>
            {f === 'all' ? `All ${items.length}` : `${f === 'event' ? 'Run events' : f === 'model' ? 'Model' : 'Tools'} ${count(f)}`}
          </button>
        ))}
      </div>
      <span className="note">The model asks for every tool call; code checks each one and runs it exactly as asked. Click a row for everything sent and returned.</span>
      {shown.map((it) => (
        <details key={`${it.call ? 'c' : 'e'}${it.call?.seq ?? it.event!.seq}`} className="call">
          <summary>
            <span className="mono hide-sm">+{((Date.parse(it.at) - t0) / 1000).toFixed(3)} s</span>
            <span className="mono hide-sm">{it.call?.stepId ?? it.event?.stepId ?? 'run'}</span>
            <span className={`kind ${it.call?.kind ?? 'event'}`}>{it.call?.kind ?? 'event'}</span>
            <b className="mono">{it.call?.name ?? it.event!.type}</b>
            <span className={it.call && !it.call.ok ? 'err' : ''}>{summary(it)}</span>
            <span className="mono st">{it.call ? `${it.call.durationMs} ms` : ''}</span>
          </summary>
          <div className="body">
            <Detail it={it} />
          </div>
        </details>
      ))}
    </section>
  );
}
